import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import { useApi } from "../lib/api";
import type {
  NotificationEventType,
  NotificationRow,
} from "../lib/types";

interface FeedResponse {
  notifications: NotificationRow[];
  unread_count: number;
}

const EVENT_LABELS: Record<NotificationEventType, string> = {
  invite_created: "New invite",
  report_submitted: "New report",
  report_approved: "Report approved",
  report_rejected: "Report rejected",
  more_info_requested: "More info requested",
  alert_published: "Alert published",
};

const POLL_MS = 60_000;

export default function NotificationBell() {
  const { isSignedIn } = useAuth();
  const api = useApi();
  const [open, setOpen] = useState(false);
  const [feed, setFeed] = useState<FeedResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get<FeedResponse>("/api/me/notifications");
      setFeed(r);
    } catch {
      // Bell is best-effort. If the feed fails (pending user, network), just
      // render the empty bell — no toast, no error UI.
      setFeed({ notifications: [], unread_count: 0 });
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    if (!isSignedIn) return;
    void load();
    const t = setInterval(() => {
      void load();
    }, POLL_MS);
    return () => clearInterval(t);
  }, [isSignedIn, load]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (
        panelRef.current?.contains(t) ||
        buttonRef.current?.contains(t)
      )
        return;
      setOpen(false);
    };
    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, [open]);

  if (!isSignedIn) return null;

  const unread = feed?.unread_count ?? 0;

  const markAllRead = async () => {
    try {
      await api.post("/api/me/notifications/read-all", {});
      await load();
    } catch {
      // ignore
    }
  };

  const markRead = async (id: number) => {
    try {
      await api.patch(`/api/me/notifications/${id}/read`, {});
      await load();
    } catch {
      // ignore
    }
  };

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        aria-label={`Notifications${unread ? ` (${unread} unread)` : ""}`}
        aria-expanded={open}
        onClick={() => setOpen((s) => !s)}
        className="relative rounded-md border border-white/10 p-2 text-slate-200 hover:bg-white/5"
      >
        <BellIcon />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-accent px-1 text-[10px] font-semibold text-ink-950">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          ref={panelRef}
          role="dialog"
          aria-label="Notifications"
          className="absolute right-0 z-40 mt-2 w-80 overflow-hidden rounded-lg border border-white/10 bg-ink-950/95 shadow-soft backdrop-blur"
        >
          <header className="flex items-center justify-between border-b border-white/5 px-4 py-2.5">
            <span className="text-sm font-semibold text-white">
              Notifications
            </span>
            {unread > 0 && (
              <button
                type="button"
                onClick={() => void markAllRead()}
                className="text-xs text-accent hover:underline"
              >
                Mark all read
              </button>
            )}
          </header>
          <div className="max-h-96 overflow-y-auto">
            {loading && !feed && (
              <div className="flex items-center gap-2 px-4 py-3 text-xs text-slate-400">
                <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-accent" />
                Loading…
              </div>
            )}
            {feed && feed.notifications.length === 0 && (
              <div className="px-4 py-6 text-center text-xs text-slate-400">
                No notifications yet.
              </div>
            )}
            <ul className="divide-y divide-white/5">
              {feed?.notifications.map((n) => (
                <NotificationItem
                  key={n.id}
                  row={n}
                  onMarkRead={() => void markRead(n.id)}
                />
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

function NotificationItem({
  row,
  onMarkRead,
}: {
  row: NotificationRow;
  onMarkRead: () => void;
}) {
  const unread = row.read_at == null;
  let payload: Record<string, unknown> = {};
  try {
    payload = row.payload_json ? JSON.parse(row.payload_json) : {};
  } catch {
    payload = {};
  }
  const label = EVENT_LABELS[row.event_type] ?? row.event_type;
  const subtitle =
    typeof payload.title === "string"
      ? payload.title
      : typeof payload.email === "string"
        ? payload.email
        : null;
  return (
    <li
      className={`grid gap-1 px-4 py-3 text-xs ${unread ? "bg-white/[0.03]" : ""}`}
    >
      <div className="flex items-center justify-between gap-2">
        <span
          className={`font-semibold ${unread ? "text-white" : "text-slate-300"}`}
        >
          {label}
        </span>
        <span className="text-slate-500">{row.created_at}</span>
      </div>
      {subtitle && (
        <p className="truncate text-slate-300" title={subtitle}>
          {subtitle}
        </p>
      )}
      {unread && (
        <button
          type="button"
          onClick={onMarkRead}
          className="justify-self-end text-[11px] text-accent hover:underline"
        >
          Mark read
        </button>
      )}
    </li>
  );
}

function BellIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M6 8a6 6 0 1 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
      <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
    </svg>
  );
}
